import { useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation } from '@tanstack/react-query'
import {
  ArrowLeft, ChevronRight, ChevronLeft, Check, AlertTriangle,
  Syringe, Shield, Search, CheckCircle, XCircle, MinusCircle,
  Radio, AlertCircle
} from 'lucide-react'
import {
  getCattle, getTreatments, getVaccines, createTreatmentSession
} from '../../services/api'
import RfidScanModal from '../../components/RfidScanModal'
import { useAuth } from '../../contexts/AuthContext'

// =============================================================================
// STEP INDICATOR
// =============================================================================

function StepIndicator({ currentStep }) {
  const steps = ['Setup', 'Select Animals', 'Review', 'Results']
  return (
    <div className="flex items-center justify-center mb-8">
      {steps.map((label, i) => (
        <div key={label} className="flex items-center">
          <div className={`flex items-center gap-2 px-3 py-2 rounded-full text-sm font-medium ${
            i < currentStep
              ? 'bg-green-100 text-green-700'
              : i === currentStep
                ? 'bg-primary-100 text-primary-700'
                : 'bg-gray-100 text-gray-500'
          }`}>
            {i < currentStep ? (
              <Check className="h-4 w-4" />
            ) : (
              <span className="h-5 w-5 rounded-full bg-current/20 flex items-center justify-center text-xs">
                {i + 1}
              </span>
            )}
            {label}
          </div>
          {i < steps.length - 1 && (
            <ChevronRight className="h-5 w-5 mx-1 text-gray-400" />
          )}
        </div>
      ))}
    </div>
  )
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export default function BulkTreatmentForm({ ranchId }) {
  const navigate = useNavigate()
  const { userId } = useAuth()
  const [step, setStep] = useState(0)

  // Step 0: Session setup
  const [sessionType, setSessionType] = useState('') // 'treatment' or 'vaccination'
  const [selectedItemId, setSelectedItemId] = useState('')
  const [otherName, setOtherName] = useState('')  // required detail when 'Other' is chosen
  const [sessionDate, setSessionDate] = useState(new Date().toISOString().split('T')[0])
  const [batchNumber, setBatchNumber] = useState('')
  const [notes, setNotes] = useState('')

  // Step 1: Animal selection
  const [selectedAnimalIds, setSelectedAnimalIds] = useState(new Set())
  const [searchQuery, setSearchQuery] = useState('')
  const [herdFilter, setHerdFilter] = useState('')
  const [categoryFilter, setCategoryFilter] = useState('')
  const [scanModalOpen, setScanModalOpen] = useState(false)

  // Step 2: Review overrides
  const [excludedAnimals, setExcludedAnimals] = useState({}) // { animalId: reason }
  const [dosageOverrides, setDosageOverrides] = useState({}) // { animalId: dosage }

  // Step 3: Results
  const [results, setResults] = useState(null)

  // Fetch reference data
  const { data: treatments } = useQuery({
    queryKey: ['treatments'],
    queryFn: () => getTreatments(),
    enabled: sessionType === 'treatment',
  })

  const { data: vaccines } = useQuery({
    queryKey: ['vaccines'],
    queryFn: () => getVaccines(),
    enabled: sessionType === 'vaccination',
  })

  // Fetch ALL active cattle by paging through the list endpoint.
  // The list API caps `limit` at 500, so a single request silently dropped
  // every animal past the first 500 — a herd of 1,497 looked like 500 and the
  // other ~997 were never offered for treatment. Page until we've pulled the
  // full `total` so "select all" really means all.
  const { data: cattleData } = useQuery({
    queryKey: ['cattle', ranchId, 'active', 'all-pages'],
    queryFn: async () => {
      const pageSize = 500
      let offset = 0
      let all = []
      let total = Infinity
      while (offset < total) {
        const res = await getCattle(ranchId, { status: 'active', limit: pageSize, offset })
        const items = res.items || res || []
        all = all.concat(items)
        total = res.total ?? all.length
        if (items.length < pageSize) break
        offset += pageSize
      }
      return { items: all, total: total === Infinity ? all.length : total }
    },
    enabled: step >= 1,
  })

  const allAnimals = useMemo(() => {
    if (!cattleData) return []
    return cattleData.items || cattleData || []
  }, [cattleData])

  // Coverage guard: surface how many animals were loaded and warn loudly if
  // we ever loaded fewer than the ranch's reported total — so silent
  // truncation can never masquerade as "all animals" again.
  const reportedTotal = cattleData?.total ?? allAnimals.length
  const coverageShort = allAnimals.length < reportedTotal

  // Get the selected treatment/vaccine reference data
  const selectedItem = useMemo(() => {
    if (sessionType === 'treatment' && treatments) {
      return treatments.find(t => t.id === selectedItemId)
    }
    if (sessionType === 'vaccination' && vaccines) {
      return vaccines.find(v => v.id === selectedItemId)
    }
    return null
  }, [sessionType, selectedItemId, treatments, vaccines])

  // 'Other' escape-hatch: force a free-text specification before proceeding.
  const isOther = !!selectedItem && (selectedItem.treatment_name === 'Other' || selectedItem.name === 'Other')

  // Filtered animals for Step 1
  const filteredAnimals = useMemo(() => {
    let animals = allAnimals
    if (searchQuery) {
      const q = searchQuery.toLowerCase()
      animals = animals.filter(a =>
        (a.local_name && a.local_name.toLowerCase().includes(q)) ||
        (a.rfid_tag && a.rfid_tag.toLowerCase().includes(q)) ||
        (a.tag_id && a.tag_id.toLowerCase().includes(q))
      )
    }
    if (herdFilter) {
      animals = animals.filter(a => a.herd === herdFilter)
    }
    if (categoryFilter) {
      animals = animals.filter(a => a.category === categoryFilter)
    }
    return animals
  }, [allAnimals, searchQuery, herdFilter, categoryFilter])

  // Selected animals for Step 2
  const selectedAnimals = useMemo(() => {
    return allAnimals.filter(a => selectedAnimalIds.has(a.id))
  }, [allAnimals, selectedAnimalIds])

  // Calculate dosage for an animal
  const calculateDosage = (animal) => {
    if (dosageOverrides[animal.id] !== undefined) {
      return dosageOverrides[animal.id]
    }
    if (sessionType === 'vaccination' && selectedItem?.standard_dosage) {
      return selectedItem.standard_dosage
    }
    if (sessionType === 'treatment' && selectedItem?.standard_dosage_per_kg && animal.last_weight_kg) {
      return Math.round(selectedItem.standard_dosage_per_kg * animal.last_weight_kg * 10) / 10
    }
    return null
  }

  // Submit mutation
  const submitMutation = useMutation({
    mutationFn: (data) => createTreatmentSession(data),
    onSuccess: (data) => {
      setResults(data)
      setStep(3)
    },
  })

  const handleSubmit = () => {
    const animals = selectedAnimals.map(a => ({
      animal_id: a.id,
      dosage: dosageOverrides[a.id] !== undefined ? dosageOverrides[a.id] : undefined,
      dosage_override: dosageOverrides[a.id] !== undefined,
      excluded: !!excludedAnimals[a.id],
      exclude_reason: excludedAnimals[a.id] || undefined,
    }))

    submitMutation.mutate({
      ranch_id: ranchId,
      session_type: sessionType,
      session_date: sessionDate,
      treatment_id: sessionType === 'treatment' ? selectedItemId : undefined,
      vaccine_id: sessionType === 'vaccination' ? selectedItemId : undefined,
      batch_number: batchNumber || undefined,
      notes: [isOther && otherName.trim() ? `Specified (Other): ${otherName.trim()}` : null, notes || null].filter(Boolean).join(' | ') || undefined,
      administered_by: userId,
      animals,
    })
  }

  // RFID continuous scan handler
  const handleAnimalScanned = (animal) => {
    setSelectedAnimalIds(prev => {
      const next = new Set(prev)
      if (next.has(animal.id)) {
        next.delete(animal.id)
      } else {
        next.add(animal.id)
      }
      return next
    })
  }

  const toggleAnimal = (animalId) => {
    setSelectedAnimalIds(prev => {
      const next = new Set(prev)
      if (next.has(animalId)) {
        next.delete(animalId)
      } else {
        next.add(animalId)
      }
      return next
    })
  }

  const selectAllFiltered = () => {
    setSelectedAnimalIds(prev => {
      const next = new Set(prev)
      filteredAnimals.forEach(a => next.add(a.id))
      return next
    })
  }

  const deselectAll = () => {
    setSelectedAnimalIds(new Set())
  }

  const toggleExclude = (animalId) => {
    setExcludedAnimals(prev => {
      const next = { ...prev }
      if (next[animalId] !== undefined) {
        delete next[animalId]
      } else {
        next[animalId] = ''
      }
      return next
    })
  }

  // Validation
  const canProceedStep0 = sessionType && selectedItemId && sessionDate && (!isOther || otherName.trim())
  const canProceedStep1 = selectedAnimalIds.size > 0
  const activeCount = selectedAnimals.length - Object.keys(excludedAnimals).length

  // Unique values for filters
  const herds = [...new Set(allAnimals.map(a => a.herd).filter(Boolean))]
  const categories = [...new Set(allAnimals.map(a => a.category).filter(Boolean))]

  return (
    <div className="max-w-5xl mx-auto p-6">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <button onClick={() => navigate('/cattle')} className="p-2 hover:bg-gray-100 rounded-lg">
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Bulk Treatment / Vaccination</h1>
          <p className="text-gray-600">Record treatments or vaccinations for multiple animals at once</p>
        </div>
      </div>

      <StepIndicator currentStep={step} />

      {/* ================================================================= */}
      {/* STEP 0: SESSION SETUP */}
      {/* ================================================================= */}
      {step === 0 && (
        <div className="space-y-6">
          {/* Type Selection */}
          <div className="grid grid-cols-2 gap-4">
            <button
              onClick={() => { setSessionType('treatment'); setSelectedItemId('') }}
              className={`p-6 rounded-lg border-2 text-left transition-all ${
                sessionType === 'treatment'
                  ? 'border-teal-500 bg-teal-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <Syringe className={`h-8 w-8 mb-3 ${sessionType === 'treatment' ? 'text-teal-600' : 'text-gray-400'}`} />
              <h3 className="font-semibold text-gray-900">Treatment</h3>
              <p className="text-sm text-gray-500 mt-1">Antibiotics, antiparasitics, supplements</p>
              <p className="text-xs text-gray-400 mt-2">Dosage calculated per animal weight</p>
            </button>
            <button
              onClick={() => { setSessionType('vaccination'); setSelectedItemId('') }}
              className={`p-6 rounded-lg border-2 text-left transition-all ${
                sessionType === 'vaccination'
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <Shield className={`h-8 w-8 mb-3 ${sessionType === 'vaccination' ? 'text-blue-600' : 'text-gray-400'}`} />
              <h3 className="font-semibold text-gray-900">Vaccination</h3>
              <p className="text-sm text-gray-500 mt-1">Preventive vaccines and boosters</p>
              <p className="text-xs text-gray-400 mt-2">Fixed dosage for all animals</p>
            </button>
          </div>

          {/* Item Selection */}
          {sessionType && (
            <div className="bg-white rounded-lg shadow p-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {sessionType === 'treatment' ? 'Select Treatment' : 'Select Vaccine'}
              </label>
              <select
                value={selectedItemId}
                onChange={(e) => { setSelectedItemId(e.target.value); setOtherName('') }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
              >
                <option value="">-- Choose --</option>
                {sessionType === 'treatment' && treatments?.map(t => (
                  <option key={t.id} value={t.id}>{t.treatment_name} ({t.drug_name})</option>
                ))}
                {sessionType === 'vaccination' && vaccines?.map(v => (
                  <option key={v.id} value={v.id}>{v.name}</option>
                ))}
              </select>

              {isOther && (
                <div className="mt-3">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Specify {sessionType === 'treatment' ? 'treatment' : 'vaccine'} *
                  </label>
                  <input
                    type="text"
                    required
                    value={otherName}
                    onChange={(e) => setOtherName(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                    placeholder={`Enter the ${sessionType === 'treatment' ? 'treatment' : 'vaccine'} name`}
                  />
                </div>
              )}

              {/* Reference info display */}
              {selectedItem && (
                <div className="mt-4 p-4 bg-gray-50 rounded-lg space-y-2">
                  {sessionType === 'treatment' ? (
                    <>
                      <p className="text-sm"><span className="text-gray-500">Drug:</span> {selectedItem.drug_name}</p>
                      <p className="text-sm"><span className="text-gray-500">Dosage:</span> {selectedItem.standard_dosage_per_kg} {selectedItem.dosage_unit}/kg body weight</p>
                      {selectedItem.manufacturer && (
                        <p className="text-sm"><span className="text-gray-500">Manufacturer:</span> {selectedItem.manufacturer}</p>
                      )}
                      {selectedItem.withdrawal_period_days > 0 && (
                        <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded text-sm text-amber-800">
                          <AlertTriangle className="inline h-4 w-4 mr-1" />
                          Withdrawal period: {selectedItem.withdrawal_period_days} days
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      <p className="text-sm"><span className="text-gray-500">Standard dosage:</span> {selectedItem.standard_dosage} {selectedItem.dosage_unit}</p>
                      {selectedItem.manufacturer && (
                        <p className="text-sm"><span className="text-gray-500">Manufacturer:</span> {selectedItem.manufacturer}</p>
                      )}
                      {selectedItem.target_disease && (
                        <p className="text-sm"><span className="text-gray-500">Target:</span> {selectedItem.target_disease}</p>
                      )}
                    </>
                  )}
                </div>
              )}

              {/* Date and batch */}
              <div className="grid grid-cols-2 gap-4 mt-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Session Date</label>
                  <input
                    type="date"
                    value={sessionDate}
                    onChange={(e) => setSessionDate(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Batch Number</label>
                  <input
                    type="text"
                    value={batchNumber}
                    onChange={(e) => setBatchNumber(e.target.value)}
                    placeholder="e.g., LOT-2026-001"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Next button */}
          <div className="flex justify-end">
            <button
              onClick={() => setStep(1)}
              disabled={!canProceedStep0}
              className="flex items-center gap-2 px-6 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next: Select Animals <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* ================================================================= */}
      {/* STEP 1: ANIMAL SELECTION */}
      {/* ================================================================= */}
      {step === 1 && (
        <div className="space-y-4">
          {/* Session summary bar */}
          <div className="bg-white rounded-lg shadow p-4 flex items-center justify-between">
            <div className="text-sm text-gray-600">
              <span className="font-medium">{sessionType === 'treatment' ? selectedItem?.treatment_name : selectedItem?.name}</span>
              {' '}&middot; {sessionDate}
              {batchNumber && <> &middot; Batch: {batchNumber}</>}
            </div>
            <div className="text-sm font-medium text-teal-700">
              {selectedAnimalIds.size} of {allAnimals.length} selected
            </div>
          </div>

          {/* Filters */}
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex flex-wrap gap-3 items-center">
              <div className="flex-1 min-w-[200px]">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search by name or RFID..."
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                  />
                </div>
              </div>
              <select
                value={herdFilter}
                onChange={(e) => setHerdFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
              >
                <option value="">All Herds</option>
                {herds.map(h => <option key={h} value={h}>Herd {h}</option>)}
              </select>
              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
              >
                <option value="">All Categories</option>
                {categories.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
              <button onClick={selectAllFiltered} className="px-3 py-2 bg-green-100 text-green-700 rounded-lg text-sm hover:bg-green-200">
                Select All ({filteredAnimals.length})
              </button>
              <button onClick={deselectAll} className="px-3 py-2 bg-gray-100 text-gray-600 rounded-lg text-sm hover:bg-gray-200">
                Deselect All
              </button>
              <button
                onClick={() => setScanModalOpen(true)}
                className="flex items-center gap-2 px-3 py-2 bg-blue-100 text-blue-700 rounded-lg text-sm hover:bg-blue-200"
              >
                <Radio className="h-4 w-4" />
                RFID Scan
              </button>
            </div>
          </div>

          {/* Coverage line: prove how many of the herd we actually loaded so a
              capped fetch can never silently pass off a subset as "all". */}
          <div className={`mb-2 text-sm flex items-center gap-2 ${coverageShort ? 'text-amber-700' : 'text-gray-500'}`}>
            {coverageShort && <AlertTriangle className="h-4 w-4" />}
            {coverageShort
              ? `Loaded only ${allAnimals.length} of ${reportedTotal} active animals — reload before treating to avoid missing animals.`
              : `${allAnimals.length} active animals loaded${searchQuery || herdFilter || categoryFilter ? ` · ${filteredAnimals.length} match filters` : ''}.`}
          </div>

          {/* Animal list */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="max-h-[400px] overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="px-4 py-3 text-left w-10"></th>
                    <th className="px-4 py-3 text-left">Name</th>
                    <th className="px-4 py-3 text-left">RFID</th>
                    <th className="px-4 py-3 text-left">Category</th>
                    <th className="px-4 py-3 text-left">Herd</th>
                    <th className="px-4 py-3 text-right">Weight (kg)</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {filteredAnimals.map(animal => {
                    const isSelected = selectedAnimalIds.has(animal.id)
                    const noWeight = !animal.last_weight_kg && sessionType === 'treatment'
                    return (
                      <tr
                        key={animal.id}
                        onClick={() => toggleAnimal(animal.id)}
                        className={`cursor-pointer transition-colors ${
                          isSelected ? 'bg-teal-50' : 'hover:bg-gray-50'
                        }`}
                      >
                        <td className="px-4 py-2">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleAnimal(animal.id)}
                            className="h-4 w-4 rounded text-teal-600"
                          />
                        </td>
                        <td className="px-4 py-2 font-medium">{animal.local_name || animal.tag_id || '-'}</td>
                        <td className="px-4 py-2 font-mono text-xs text-gray-500">{animal.rfid_tag || '-'}</td>
                        <td className="px-4 py-2 capitalize">{animal.category}</td>
                        <td className="px-4 py-2">{animal.herd || '-'}</td>
                        <td className="px-4 py-2 text-right">
                          {animal.last_weight_kg ? (
                            `${animal.last_weight_kg}`
                          ) : noWeight ? (
                            <span className="text-amber-600 flex items-center justify-end gap-1">
                              <AlertTriangle className="h-3 w-3" /> No weight
                            </span>
                          ) : '-'}
                        </td>
                      </tr>
                    )
                  })}
                  {filteredAnimals.length === 0 && (
                    <tr>
                      <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                        No animals match your filters
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Navigation */}
          <div className="flex justify-between">
            <button onClick={() => setStep(0)} className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg">
              <ChevronLeft className="h-4 w-4" /> Back
            </button>
            <button
              onClick={() => setStep(2)}
              disabled={!canProceedStep1}
              className="flex items-center gap-2 px-6 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next: Review ({selectedAnimalIds.size} animals) <ChevronRight className="h-4 w-4" />
            </button>
          </div>

          {/* RFID Scan Modal */}
          <RfidScanModal
            isOpen={scanModalOpen}
            onClose={() => setScanModalOpen(false)}
            onAnimalScanned={handleAnimalScanned}
            scanCount={selectedAnimalIds.size}
            ranchId={ranchId}
          />
        </div>
      )}

      {/* ================================================================= */}
      {/* STEP 2: REVIEW & CONFIRM */}
      {/* ================================================================= */}
      {step === 2 && (
        <div className="space-y-4">
          {/* Withdrawal warning */}
          {sessionType === 'treatment' && selectedItem?.withdrawal_period_days > 0 && (
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-amber-800">
                  Withdrawal Period: {selectedItem.withdrawal_period_days} days
                </p>
                <p className="text-sm text-amber-700">
                  Animals treated in this session should not be sold before{' '}
                  {new Date(new Date(sessionDate).getTime() + selectedItem.withdrawal_period_days * 86400000).toLocaleDateString()}
                </p>
              </div>
            </div>
          )}

          {/* Summary bar */}
          <div className="bg-white rounded-lg shadow p-4 flex items-center justify-between">
            <div className="text-sm text-gray-600">
              <span className="font-medium">{sessionType === 'treatment' ? selectedItem?.treatment_name : selectedItem?.name}</span>
              {' '}&middot; {sessionDate} &middot; {selectedAnimals.length} animals
            </div>
            <div className="text-sm">
              <span className="text-green-700 font-medium">Will treat: {activeCount}</span>
              {Object.keys(excludedAnimals).length > 0 && (
                <span className="text-amber-600 ml-3">{Object.keys(excludedAnimals).length} excluded</span>
              )}
            </div>
          </div>

          {/* Review table */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="max-h-[400px] overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="px-4 py-3 text-left w-10">Include</th>
                    <th className="px-4 py-3 text-left">Name</th>
                    <th className="px-4 py-3 text-left">RFID</th>
                    <th className="px-4 py-3 text-left">Category</th>
                    <th className="px-4 py-3 text-right">Weight</th>
                    <th className="px-4 py-3 text-right">Dosage ({selectedItem?.dosage_unit || 'ml'})</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {selectedAnimals.map(animal => {
                    const isExcluded = excludedAnimals[animal.id] !== undefined
                    const dosage = calculateDosage(animal)
                    const hasOverride = dosageOverrides[animal.id] !== undefined

                    return (
                      <tr key={animal.id} className={isExcluded ? 'bg-gray-50 opacity-60' : ''}>
                        <td className="px-4 py-2">
                          <input
                            type="checkbox"
                            checked={!isExcluded}
                            onChange={() => toggleExclude(animal.id)}
                            className="h-4 w-4 rounded text-teal-600"
                          />
                        </td>
                        <td className="px-4 py-2 font-medium">{animal.local_name || animal.tag_id || '-'}</td>
                        <td className="px-4 py-2 font-mono text-xs text-gray-500">{animal.rfid_tag || '-'}</td>
                        <td className="px-4 py-2 capitalize">{animal.category}</td>
                        <td className="px-4 py-2 text-right">{animal.last_weight_kg || '-'}</td>
                        <td className="px-4 py-2 text-right">
                          {isExcluded ? (
                            <span className="text-gray-400">-</span>
                          ) : dosage != null ? (
                            <input
                              type="number"
                              step="0.1"
                              value={hasOverride ? dosageOverrides[animal.id] : dosage}
                              onChange={(e) => {
                                const val = parseFloat(e.target.value)
                                if (!isNaN(val) && val > 0) {
                                  setDosageOverrides(prev => ({ ...prev, [animal.id]: val }))
                                }
                              }}
                              className={`w-20 px-2 py-1 border rounded text-right text-sm ${
                                hasOverride ? 'border-amber-300 bg-amber-50' : 'border-gray-200'
                              }`}
                            />
                          ) : (
                            <span className="text-amber-600 text-xs flex items-center justify-end gap-1">
                              <AlertCircle className="h-3 w-3" /> No weight
                            </span>
                          )}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Notes */}
          <div className="bg-white rounded-lg shadow p-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Session Notes (optional)</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              placeholder="Any notes about this session..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
            />
          </div>

          {/* Navigation */}
          <div className="flex justify-between">
            <button onClick={() => setStep(1)} className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg">
              <ChevronLeft className="h-4 w-4" /> Back
            </button>
            <button
              onClick={handleSubmit}
              disabled={submitMutation.isPending || activeCount === 0}
              className="flex items-center gap-2 px-6 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50"
            >
              {submitMutation.isPending ? 'Submitting...' : `Submit ${activeCount} ${sessionType === 'treatment' ? 'Treatments' : 'Vaccinations'}`}
            </button>
          </div>

          {submitMutation.isError && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {submitMutation.error?.response?.data?.detail || submitMutation.error?.message || 'Submission failed'}
            </div>
          )}
        </div>
      )}

      {/* ================================================================= */}
      {/* STEP 3: RESULTS */}
      {/* ================================================================= */}
      {step === 3 && results && (
        <div className="space-y-6">
          {/* Summary cards */}
          <div className="grid grid-cols-4 gap-4">
            <div className="bg-white rounded-lg shadow p-4 text-center">
              <div className="text-2xl font-bold text-gray-900">{results.total_animals}</div>
              <div className="text-sm text-gray-500">Total</div>
            </div>
            <div className="bg-green-50 rounded-lg shadow p-4 text-center">
              <div className="text-2xl font-bold text-green-700">{results.successful_animals}</div>
              <div className="text-sm text-green-600">Successful</div>
            </div>
            <div className="bg-amber-50 rounded-lg shadow p-4 text-center">
              <div className="text-2xl font-bold text-amber-700">{results.skipped_animals}</div>
              <div className="text-sm text-amber-600">Excluded/Skipped</div>
            </div>
            <div className="bg-red-50 rounded-lg shadow p-4 text-center">
              <div className="text-2xl font-bold text-red-700">{results.error_animals}</div>
              <div className="text-sm text-red-600">Errors</div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-4">
            <p className="text-sm text-gray-600 mb-1">
              <span className="font-medium">{results.treatment_or_vaccine_name}</span>
              {' '}&middot; {results.session_type} session
            </p>
            <p className="text-xs text-gray-400">
              Events created as pending — they will appear in the manager approval queue.
            </p>
          </div>

          {/* Results table */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left w-10"></th>
                  <th className="px-4 py-3 text-left">Animal</th>
                  <th className="px-4 py-3 text-left">RFID</th>
                  <th className="px-4 py-3 text-right">Dosage</th>
                  <th className="px-4 py-3 text-left">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {results.results.map((r, i) => (
                  <tr key={i}>
                    <td className="px-4 py-2">
                      {r.status === 'success' ? <CheckCircle className="h-4 w-4 text-green-500" /> :
                       r.status === 'excluded' ? <MinusCircle className="h-4 w-4 text-amber-500" /> :
                       r.status === 'skipped' ? <AlertTriangle className="h-4 w-4 text-amber-500" /> :
                       <XCircle className="h-4 w-4 text-red-500" />}
                    </td>
                    <td className="px-4 py-2 font-medium">{r.animal_name || '-'}</td>
                    <td className="px-4 py-2 font-mono text-xs text-gray-500">{r.rfid_tag || '-'}</td>
                    <td className="px-4 py-2 text-right">
                      {r.dosage ? `${r.dosage} ${r.dosage_unit || ''}` : '-'}
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-600">{r.message}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Done button */}
          <div className="flex justify-center">
            <button
              onClick={() => navigate('/cattle')}
              className="px-6 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700"
            >
              Done — Back to Cattle
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
